import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker, TrinitiTimePicker, TrinitiDateTimePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { createEntry, updateEntry } from './entriesApi';
import { BodyMapField } from './fields/BodyMapField';
import { GcsScoreField } from './fields/GcsScoreField';
import { PhotoEvidenceField } from './fields/PhotoEvidenceField';
import { LinkedEntryField } from './fields/LinkedEntryField';

// Advanced (Slice B) object-valued field types.
const ADVANCED_TYPES = new Set(['body_map', 'gcs_score', 'photo_evidence', 'linked_entry']);

function isAdvancedEmpty(type, val) {
  if (!val || typeof val !== 'object') return true;
  if (type === 'body_map') return !(Array.isArray(val.regions) && val.regions.length > 0);
  if (type === 'gcs_score') return val.eye == null || val.verbal == null || val.motor == null;
  if (type === 'photo_evidence') return !val.url;
  if (type === 'linked_entry') return !val.entry_id;
  return true;
}

/**
 * Day 10 — Chart Dynamic Entry Form
 * Renders inputs from `template.field_definitions`.
 * Supported types: text, textarea, number, select, boolean, rating, time, datetime, date.
 *
 * Design choices:
 *  - One modal implementation reused by Pain, Fluid Intake, Behaviour (ABC), Wound.
 *  - Values kept as the exact API shape (no client-side mapping layer).
 *  - Validation preview is basic (required + select options); backend is the
 *    source of truth and returns standardised error toasts.
 */

function toLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}
function fromLocalInput(local) {
  if (!local) return '';
  try { return new Date(local).toISOString(); } catch { return ''; }
}
function toDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return ''; }
}

function initialValueForField(fd, existingValue) {
  if (existingValue !== undefined && existingValue !== null) {
    if (fd.type === 'datetime') return toLocalInput(existingValue);
    if (fd.type === 'date') return toDateInput(existingValue);
    if (fd.type === 'boolean') return !!existingValue;
    return existingValue;
  }
  if (fd.type === 'boolean') return !!fd.default_value;
  if (fd.default_value !== undefined && fd.default_value !== null) return fd.default_value;
  return '';
}

function RatingInput({ value, min = 0, max = 10, onChange, testId }) {
  const safe = Number.isFinite(Number(value)) ? Number(value) : '';
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min={min}
        max={max}
        step={1}
        value={safe}
        onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="w-20"
        data-testid={testId}
      />
      <span className="text-xs text-muted-foreground">/ {max}</span>
    </div>
  );
}

export function ChartDynamicEntryForm({
  open, onOpenChange, participantId, chartId, template, entry = null, onSaved, canViewHighRisk = true,
}) {
  const isEdit = !!entry?.id;
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({});

  const fieldDefs = useMemo(() => template?.field_definitions || [], [template]);

  useEffect(() => {
    if (!open || !template) return;
    const existing = entry?.values || {};
    const next = {};
    for (const fd of fieldDefs) {
      next[fd.key] = initialValueForField(fd, existing[fd.key]);
    }
    setState(next);
  }, [open, template, entry, fieldDefs]);

  const set = (key, value) => setState(s => ({ ...s, [key]: value }));

  const buildPayload = () => {
    const out = {};
    for (const fd of fieldDefs) {
      const raw = state[fd.key];
      if (ADVANCED_TYPES.has(fd.type)) {
        if (isAdvancedEmpty(fd.type, raw)) {
          if (fd.required) throw new Error(`${fd.label || fd.key} is required`);
          continue;
        }
        out[fd.key] = raw;
        continue;
      }
      if (raw === undefined || raw === '' || raw === null) {
        if (fd.required) {
          throw new Error(`${fd.label || fd.key} is required`);
        }
        continue;
      }
      if (fd.type === 'datetime') out[fd.key] = fromLocalInput(raw);
      else if (fd.type === 'number' || fd.type === 'rating') out[fd.key] = Number(raw);
      else if (fd.type === 'boolean') out[fd.key] = !!raw;
      else out[fd.key] = raw;
    }
    return out;
  };

  const submit = async () => {
    let payload;
    try { payload = buildPayload(); }
    catch (e) { return toast.error(e.message); }
    try {
      setBusy(true);
      if (isEdit) await updateEntry(participantId, chartId, entry.id, payload);
      else await createEntry(participantId, chartId, payload);
      toast.success(isEdit ? 'Entry updated' : 'Entry saved');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to save entry');
    } finally { setBusy(false); }
  };

  if (!template) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="dynamic-template-loading">
            <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading template…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dynamic-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-slate-600" />
            {isEdit ? `Edit ${template.name} entry` : `Record ${template.name}`}
          </DialogTitle>
          <DialogDescription>
            Fields render from the template schema. Backend is the source of truth for validation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fieldDefs.map(fd => {
            const tid = `dyn-field-${fd.key}`;
            const label = (
              <Label htmlFor={tid} className="text-xs">
                {fd.label || fd.key}{fd.required ? ' *' : ''}
              </Label>
            );
            const value = state[fd.key] ?? '';
            if (fd.type === 'text') {
              return (
                <div key={fd.key}>
                  {label}
                  <Input
                    id={tid} value={value} onChange={e => set(fd.key, e.target.value)}
                    maxLength={fd.max_length || 500} placeholder={fd.placeholder || ''}
                    className="mt-1" data-testid={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'textarea') {
              return (
                <div key={fd.key}>
                  {label}
                  <Textarea
                    id={tid} value={value} onChange={e => set(fd.key, e.target.value)}
                    maxLength={fd.max_length || 2000} rows={3}
                    className="mt-1" data-testid={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'number') {
              return (
                <div key={fd.key}>
                  {label}
                  <Input
                    id={tid} type="number"
                    min={fd.min} max={fd.max} step={fd.step || 1}
                    value={value} onChange={e => set(fd.key, e.target.value)}
                    className="mt-1" data-testid={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'rating') {
              return (
                <div key={fd.key}>
                  {label}
                  <div className="mt-1">
                    <RatingInput
                      value={value} min={fd.min ?? 0} max={fd.max ?? 10}
                      onChange={v => set(fd.key, v)} testId={tid}
                    />
                  </div>
                </div>
              );
            }
            if (fd.type === 'select') {
              return (
                <div key={fd.key}>
                  {label}
                  <Select value={value || undefined} onValueChange={v => set(fd.key, v)}>
                    <SelectTrigger id={tid} className="mt-1" data-testid={tid}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {(fd.options || []).map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (fd.type === 'boolean') {
              return (
                <div key={fd.key} className="flex items-center justify-between border rounded-md px-3 py-2">
                  <Label htmlFor={tid} className="text-xs">{fd.label || fd.key}</Label>
                  <Switch
                    id={tid} checked={!!value} onCheckedChange={v => set(fd.key, v)}
                    data-testid={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'datetime') {
              return (
                <div key={fd.key}>
                  {label}
                  <TrinitiDateTimePicker
                    id={tid} value={value}
                    onChange={v => set(fd.key, v)}
                    className="mt-1" testId={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'date') {
              return (
                <div key={fd.key}>
                  {label}
                  <TrinitiDatePicker
                    id={tid} value={value}
                    onChange={v => set(fd.key, v)}
                    className="mt-1" testId={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'time') {
              return (
                <div key={fd.key}>
                  {label}
                  <TrinitiTimePicker
                    id={tid} value={value}
                    onChange={v => set(fd.key, v)}
                    className="mt-1" testId={tid}
                  />
                </div>
              );
            }
            if (fd.type === 'body_map') {
              return (
                <div key={fd.key}>
                  {label}
                  <div className="mt-1">
                    <BodyMapField value={state[fd.key]} onChange={v => set(fd.key, v)} />
                  </div>
                </div>
              );
            }
            if (fd.type === 'gcs_score') {
              return (
                <div key={fd.key}>
                  {label}
                  <div className="mt-1">
                    <GcsScoreField value={state[fd.key]} onChange={v => set(fd.key, v)} />
                  </div>
                </div>
              );
            }
            if (fd.type === 'photo_evidence') {
              return (
                <div key={fd.key}>
                  {label}
                  <div className="mt-1">
                    <PhotoEvidenceField value={state[fd.key]} onChange={v => set(fd.key, v)} canViewHighRisk={canViewHighRisk} participantId={participantId} chartId={chartId} />
                  </div>
                </div>
              );
            }
            if (fd.type === 'linked_entry') {
              return (
                <div key={fd.key}>
                  {label}
                  <div className="mt-1">
                    <LinkedEntryField fd={fd} value={state[fd.key]} onChange={v => set(fd.key, v)} participantId={participantId} />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dyn-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="dyn-submit-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Save entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChartDynamicEntryForm;
